import { STATE_CODE_TO_LABEL } from '../../constants/index';
import { DAPI_HOST, BASE_URL } from '../../config/index';
import { apiGetJson, apiPostJson } from '~/core/dapi';

export type SupportedStatesResponse = {
  supported_states: string[];
};

/**
 * Gets a list of supported states
 *
 * @returns a list of state codes and labels
 */
export async function getSupportedStates() {
  const response = await apiPostJson<SupportedStatesResponse>(
    `${DAPI_HOST}/v1/membership/supported-states`
  );

  if (response.errors) {
    throw Error(response.errors[0].description);
  }

  return response.data.supported_states.sort().map((stateCode) => ({
    stateCode,
    label: STATE_CODE_TO_LABEL[stateCode.toLowerCase() as keyof typeof STATE_CODE_TO_LABEL],
  }));
}
export type ValidZipResponse = {
  is_valid_zip: boolean;
};

/**
 * Checks if a given zip code is guaranteed to be supported by solv prime.
 * (Some zip codes can be in two states).
 *
 * @returns if the zip is in a supported state
 */
export async function checkIfZipIsValid(zip: string) {
  const response = await apiPostJson<ValidZipResponse>(
    `${DAPI_HOST}/v1/membership/check-availability`,
    {
      zip_code: zip,
    }
  );

  if (response.errors) {
    throw Error(response.errors[0].description);
  }

  return response.data.is_valid_zip;
}

export type SetupIntentResponse = {
  setup_intent_client_secret: string;
};

/**
 * Creates a stripe setup intent and returns its client secret
 *
 * @returns the client secret
 */
export async function createSetupIntent() {
  const response = await apiPostJson<SetupIntentResponse>(
    `${DAPI_HOST}/v1/membership/create/setup-intent`,
    {
      method: 'POST',
    }
  );

  if (response.errors) {
    throw Error(response.errors[0].description);
  }

  return response.data.setup_intent_client_secret;
}

/**
 * Creates a subscription
 *
 * @returns success and subscription id
 */
export async function createSubscription(paymentMethod: string) {
  const response = await apiPostJson<{ success: boolean; stripe_subscription_id: string }>(
    `${DAPI_HOST}/v1/membership/create/subscription`,
    {
      payment_method_id: paymentMethod,
    }
  );

  return response.data;
}

export type StripeCard = {
  card: {
    brand: string;
    last4: string;
  };
};
/**
 * Get the customers payment method from stripe
 *
 * @returns A stripe card object
 */
export async function getPaymentMethod() {
  const { data } = await apiPostJson<StripeCard>(`${DAPI_HOST}/v1/membership/payment-methods`);

  return data;
}

export interface StripePortal {
  id: string;
  object: string;
  configuration: string;
  created: number;
  customer: string;
  livemode: boolean;
  on_behalf_of: null;
  return_url: string;
  url: string;
}

/**
 * Redirect to stripe billing portal.
 */
export async function redirectToStripeBillingPortal(returnUrl = `${BASE_URL}/book/finalize`) {
  const { data } = await apiGetJson<StripePortal>(
    `${DAPI_HOST}/v1/membership/portal-session?return_url=${returnUrl}`
  );
  window.location.href = data.url;
}
